<template>
  <div v-on-click-outside="closeSearch">
    <FormInput
      id="invite-teammate-search-input"
      v-model="email"
      :class="{ 'invite-teammate-search-input_open': showSearchResults }"
      :placeholder="
        placeholder || $t('Enter name, position or email to invite your teammate to participate')
      "
      @focus="openSearch"
    >
      <template #post>
        <Icon type="search" size="20" color="secondary" />
      </template>
      <template v-if="showSearchResults" v-slot:autocomplete>
        <UserSearchResultsContainer :key="email">
          <div class="search-contractors__actions">
            <div v-if="email === ''" class="spacex-12">
              <UserSearchResultsInfoText>
                {{ infoText }}
              </UserSearchResultsInfoText>
              <Button
                v-if="!isProjectModal"
                type="func"
                custom-color="blue"
                size="small"
                @click="selectAll"
              >
                {{ $t('Select all') }}
              </Button>
              <Button
                v-if="!isProjectModal"
                type="func"
                custom-color="blue"
                size="small"
                @click="deselectAll"
              >
                {{ $t('Deselect all') }}
              </Button>
              <Button type="func" custom-color="blue" size="small" @click="startInvitation">
                {{ $t('Invite new teammate') }}
              </Button>
              <Spinner v-if="isBottomActionLoading" size="20" class="float-right" />
            </div>
            <div v-else-if="membersSearchResults.length > 0" class="spacex-12">
              <UserSearchResultsInfoText>
                {{ $t(`${membersSearchResults.length} teammates found`) }}
              </UserSearchResultsInfoText>
              <Button type="func" custom-color="blue" size="small" @click="startInvitation">
                {{ $t('Invite new teammate') }}
              </Button>
            </div>
            <div v-else class="spacex-12">
              <UserSearchResultsInfoText>
                {{ $t('We haven’t found this person in your company.') }}
              </UserSearchResultsInfoText>
              <Button type="func" custom-color="blue" size="small" @click="startInvitation">
                {{ $t('Invite new teammate') }}
              </Button>
            </div>
          </div>
          <div v-if="mode === MODES.invitation" class="invite-new-user-form">
            <FormInput
              id="new-user-form"
              v-model="invitationEmail"
              :error-message="inviteError"
              :error="!!inviteError"
              type="email"
              :placeholder="$t('Enter email of new user')"
              class="mb-10"
            />
            <Button type="secondary" left-icon="plus" @click="invite">
              {{ $t('Invite new user') }}
            </Button>
          </div>
          <UserSearchResultItem v-for="member in membersSearchResults" :key="member.id">
            <Spinner
              v-if="memberLoadingUserId === member.user_id"
              size="20"
              class="search-contractors__contractor__checkbox"
            />
            <Checkbox
              v-else
              :disabled="!canIHandler(member)"
              :checked="isChecked(member)"
              class="search-contractors__contractor__checkbox"
              @check="memberClickHandler(member)"
            />
            <UserInfo :user="member.user" show-position full position-in-bottom />
          </UserSearchResultItem>
        </UserSearchResultsContainer>
      </template>
    </FormInput>
  </div>
</template>

<script>
  import teamQuery from '@/gql/projects/team.graphql';
  import FormInput from '@/components/ui/FormInput';
  import ClickOutside from 'vue-click-outside';
  import UserInfo from '@/components/ui/UserInfo';
  import Checkbox from '@/components/ui/Checkbox';
  import UserSearchResultItem from '@/components/common/UserSearchResults/UserSearchResultItem';
  import UserSearchResultsContainer from '@/components/common/UserSearchResults/UserSearchResultsContainer';
  import factory from '@/utils/api/factory';
  import Spinner from 'vue-simple-spinner';
  import parseError from '@/utils/formatting/parse-error';
  import t from '@/i18n';
  import UserSearchResultsInfoText from '@/components/common/UserSearchResults/UserSearchResultsInfoText';
  import { mapState } from 'vuex';
  import request from '@/utils/api/request';
  import canI from '@/utils/roles/access';
  import Button from '@/components/ui-v2/Button';
  import Icon from '@/components/ui/Icon';

  const MODES = {
    search: 'search',
    invitation: 'invitation',
  };

  export default {
    name: 'InviteTeammate',
    components: {
      UserSearchResultsInfoText,
      UserSearchResultsContainer,
      UserInfo,
      Checkbox,
      UserSearchResultItem,
      Spinner,
      Button,
      Icon,
      FormInput,
    },
    directives: { ClickOutside },
    props: {
      offer: {
        type: Object,
        default: () => {},
      },
      isProjectModal: {
        type: Boolean,
        default: false,
      },
      projectInfo: {
        type: Object,
        default: () => {},
      },
      placeholder: {
        type: String,
      },
    },
    apollo: {
      team() {
        return {
          query: teamQuery,
          variables() {
            return {
              id: this.$store.state.auth?.user?.membership?.id,
            };
          },
          update(data) {
            if (data?.team?.length) return data.team[0];
            return { memberships: [] };
          },
        };
      },
    },
    data() {
      return {
        mode: MODES.search,
        email: '',
        isBottomActionLoading: false,
        internalContractorResponsibleUserIds: null,
        showSearchResults: false,
        memberLoadingUserId: null,
        isAnythingChanged: false,
        isInviteLoading: false,
        inviteError: null,
        clearFieldTrigger: false,
        MODES,
        invitationEmail: '',
      };
    },
    computed: {
      ...mapState({
        activeCompany: (state) => state.auth.user?.membership,
      }),
      selectedMembersCount() {
        if (this.isProjectModal) {
          return this.projectInfo?.team?.length || 0;
        }
        return this.membersSearchResults.length;
      },
      members() {
        if (this.isProjectModal) {
          return this.team?.memberships;
        }
        return this.team?.memberships.filter(
          (member) => member.user.id !== this.offer.contractor_contact_person_user.id
        );
      },
      membersSearchResults() {
        if (this.email === '' || this.email === null) {
          return this.members;
        }
        const emailLowerCase = this.email.toLowerCase();
        return this.members.filter(
          (member) =>
            member.user.email?.toLowerCase().includes(emailLowerCase) ||
            member.user.name?.toLowerCase().includes(emailLowerCase)
        );
      },
      contractorResponsibleUserIds: {
        get() {
          return (
            this.internalContractorResponsibleUserIds ||
            this.offer?.contractor_responsible_users?.map((x) => x.user_id) ||
            []
          );
        },
        set(value) {
          this.internalContractorResponsibleUserIds = value;
        },
      },
      infoText() {
        let count;
        if (this.isProjectModal) {
          count = this.projectInfo?.team?.length || 0;
        } else {
          // remove offer's responsible user from counter
          count = this.contractorResponsibleUserIds?.filter(
            (x) => x !== this.offer?.contractor_contact_person_user?.id
          ).length;
        }
        return t(`Selected ${count} team members`);
      },
    },
    methods: {
      t,
      canIHandler(member) {
        if (member.role === 'owner') {
          return canI('editOwners');
        } else if (member.role === 'admin') {
          return canI('editAdmins');
        }
        return canI('editRegulars');
      },
      startInvitation() {
        this.mode = MODES.invitation;
      },
      cancelInvitation() {
        this.mode = MODES.search;
        this.closeSearch();
      },
      openSearch() {
        this.showSearchResults = true;
        this.isAnythingChanged = false;
      },
      updateMembers() {
        this.$emit('update-members');
      },
      closeSearch() {
        this.showSearchResults = false;
        this.contractorResponsibleUserIds = null;
        if (this.isAnythingChanged) {
          this.updateMembers();
        }
      },
      isChecked(member) {
        if (this.isProjectModal) {
          return (
            this.projectInfo.team.findIndex((localMember) => {
              return member.user.id === localMember.user.id;
            }) > -1
          );
        }
        return !!this.contractorResponsibleUserIds?.find((userId) => userId === member.user.id);
      },
      async memberClickHandler(member) {
        const userId = member.user.id;
        const isChecked = this.isChecked(member);

        this.memberLoadingUserId = userId;

        try {
          await this.save(isChecked, [userId], member.user);

          // change local state
          if (isChecked) {
            this.contractorResponsibleUserIds = this.contractorResponsibleUserIds?.filter(
              (x) => x !== userId
            );
          } else {
            this.contractorResponsibleUserIds = [...this.contractorResponsibleUserIds, userId];
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }

        this.memberLoadingUserId = null;
      },
      async save(isDelete, ids, user) {
        const method = isDelete ? 'deleteInResourcePath' : 'postInResourcePath';
        try {
          if (this.isProjectModal) {
            if (isDelete) {
              this.$emit('deny-access', ids[0], user);
            } else {
              await request.post(`/projects/${this.projectInfo.id}/give-access/`, {
                user_id: ids[0],
              });
            }
          } else {
            await factory('incoming_tenders/offers')[method](this.offer.id, 'responsible_users', {
              ids,
            });
          }
          await this.updateMembers();
          this.isAnythingChanged = true;
        } catch (e) {
          alert(parseError(e?.response?.data) || t('Unexpected error'));
          throw e;
        }
      },
      async selectAll() {
        this.isBottomActionLoading = true;
        try {
          const ids = this.members.map((x) => x.user.id);
          await this.save(false, ids);
          this.contractorResponsibleUserIds = ids;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
        this.isBottomActionLoading = false;
      },
      async deselectAll() {
        this.isBottomActionLoading = true;
        try {
          const ids = this.members.map((x) => x.user.id);
          await this.save(true, ids);
          this.contractorResponsibleUserIds = [];
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
        this.isBottomActionLoading = false;
      },
      async invite() {
        this.inviteError = null;
        this.isInviteLoading = true;
        try {
          const res = await factory('teams').postInResourcePath(this.activeCompany?.id, 'members', {
            email: this.invitationEmail,
          });
          await this.save(false, [res.data.user_id]);
          this.isAnythingChanged = true;
          this.invitationEmail = '';
          this.clearFieldTrigger = !this.clearFieldTrigger;
          this.cancelInvitation();
        } catch (e) {
          console.error(e);
          this.inviteError = parseError(e) || t('Something wrong happened');
        }
        this.isInviteLoading = false;
      },
    },
  };
</script>

<style scoped>
  .no-teammates-info-line {
    padding-bottom: 5px;
  }
</style>
